import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import dataApi from '@/services/dataApi'

export const useAppStore = defineStore('app', () => {
  // 状态
  const loading = ref(false)
  const error = ref(null)
  const jiraIssues = ref([])
  const githubPRs = ref([])
  const mergedPRs = ref([])
  const customMembers = ref([])
  const defaultPMCMembers = ref([])
  const lastUpdated = ref({
    jira: null,
    github: null,
    merged: null,
    members: null,
    defaultPMC: null
  })

  // 新增数据源和缓存状态
  const dataSource = ref({
    jira: 'unknown',
    github: 'unknown',
    merged: 'unknown',
    members: 'unknown'
  })

  const syncStatus = ref({
    lastSync: null,
    isRunning: false,
    stats: null
  })

  // 计算属性
  const totalIssues = computed(() => jiraIssues.value.length)
  const totalPRs = computed(() => githubPRs.value.length)
  const totalMergedPRs = computed(() => mergedPRs.value.length)
  const totalCustomMembers = computed(() => customMembers.value.length)

  // 操作
  const setLoading = (value) => {
    loading.value = value
  }

  const setError = (errorMessage) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  // JIRA Issues (使用优化的数据API)
  const fetchJiraIssues = async (force = false) => {
    try {
      setLoading(true)
      clearError()

      const response = await dataApi.getJiraIssues(force)

      jiraIssues.value = response.issues || []
      lastUpdated.value.jira = response.lastUpdated
      dataSource.value.jira = dataApi.formatDataSource(response.source, response.cached)

      return response
    } catch (error) {
      console.error('Error fetching JIRA issues:', error)
      setError(error.userMessage || '获取JIRA问题失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // GitHub PRs (使用优化的数据API)
  const fetchGitHubPRs = async (force = false) => {
    try {
      setLoading(true)
      clearError()

      const response = await dataApi.getOpenPRs(force)

      githubPRs.value = response.prs || []
      lastUpdated.value.github = response.lastUpdated
      dataSource.value.github = dataApi.formatDataSource(response.source, response.cached)

      return response
    } catch (error) {
      console.error('Error fetching GitHub PRs:', error)
      setError(error.userMessage || '获取GitHub PR失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Merged PRs (使用优化的数据API)
  const fetchMergedPRs = async (force = false) => {
    try {
      setLoading(true)
      clearError()

      const response = await dataApi.getMergedPRs(force)

      mergedPRs.value = response.prs || []
      lastUpdated.value.merged = response.lastUpdated
      dataSource.value.merged = dataApi.formatDataSource(response.source, response.cached)

      return response
    } catch (error) {
      console.error('Error fetching merged PRs:', error)
      setError(error.userMessage || '获取已合并PR失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Custom Members
  const fetchCustomMembers = async () => {
    try {
      setLoading(true)
      clearError()
      
      const response = await api.get('/pmc/members')
      
      customMembers.value = response.data.members || []
      lastUpdated.value.members = response.data.lastUpdated
      
      return response.data
    } catch (error) {
      console.error('Error fetching custom members:', error)
      setError('获取自定义成员失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const addCustomMember = async (memberData) => {
    try {
      setLoading(true)
      clearError()

      const response = await api.post('/pmc/members', memberData)

      // 重新获取所有成员列表以保持数据一致性
      await fetchAllMembers()

      return response.data
    } catch (error) {
      console.error('Error adding custom member:', error)
      setError('添加成员失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateCustomMember = async (id, memberData) => {
    try {
      setLoading(true)
      clearError()

      const response = await api.put(`/pmc/members/${id}`, memberData)

      // 重新获取所有成员列表以保持数据一致性
      await fetchAllMembers()

      return response.data
    } catch (error) {
      console.error('Error updating custom member:', error)
      setError('更新成员失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteCustomMember = async (id) => {
    try {
      setLoading(true)
      clearError()

      const response = await api.delete(`/pmc/members/${id}`)

      // 重新获取所有成员列表以保持数据一致性
      await fetchAllMembers()

      return response.data
    } catch (error) {
      console.error('Error deleting custom member:', error)
      setError('删除成员失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Default PMC Members
  const fetchDefaultPMCMembers = async () => {
    try {
      setLoading(true)
      clearError()
      
      const response = await api.get('/pmc/default-members')
      
      defaultPMCMembers.value = response.data.members || []
      lastUpdated.value.defaultPMC = response.data.lastUpdated
      
      return response.data
    } catch (error) {
      console.error('Error fetching default PMC members:', error)
      setError('获取默认PMC成员失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const addDefaultPMCMember = async (memberData) => {
    try {
      setLoading(true)
      clearError()

      const response = await api.post('/pmc/default-members', memberData)

      // 重新获取所有成员列表以保持数据一致性
      await fetchAllMembers()

      return response.data
    } catch (error) {
      console.error('Error adding default PMC member:', error)
      setError('添加默认PMC成员失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateDefaultPMCMember = async (id, memberData) => {
    try {
      setLoading(true)
      clearError()

      const response = await api.put(`/pmc/default-members/${id}`, memberData)

      // 重新获取所有成员列表以保持数据一致性
      await fetchAllMembers()

      return response.data
    } catch (error) {
      console.error('Error updating default PMC member:', error)
      setError('更新默认PMC成员失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteDefaultPMCMember = async (id) => {
    try {
      setLoading(true)
      clearError()

      const response = await api.delete(`/pmc/default-members/${id}`)

      // 重新获取所有成员列表以保持数据一致性
      await fetchAllMembers()

      return response.data
    } catch (error) {
      console.error('Error deleting default PMC member:', error)
      setError('删除默认PMC成员失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // 获取所有成员（默认PMC + 自定义）
  const fetchAllMembers = async () => {
    try {
      setLoading(true)
      clearError()
      
      const response = await api.get('/pmc/all-members')
      
      // 从返回的members数组中分离PMC成员和自定义成员
      const allMembers = response.data.members || []
      defaultPMCMembers.value = allMembers.filter(member => member.type === 'pmc')
      customMembers.value = allMembers.filter(member => member.type === 'custom')
      lastUpdated.value.defaultPMC = response.data.lastUpdated
      lastUpdated.value.members = response.data.lastUpdated
      
      return response.data
    } catch (error) {
      console.error('Error fetching all members:', error)
      setError('获取所有成员失败')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // 手动触发数据同步
  const triggerDataSync = async (type = 'all') => {
    try {
      setLoading(true)
      clearError()
      syncStatus.value.isRunning = true

      const response = await dataApi.triggerSync(type)

      // 同步完成后刷新相关数据
      if (response.success) {
        if (type === 'all' || type === 'jira') {
          await fetchJiraIssues(true)
        }
        if (type === 'all' || type === 'github') {
          await fetchGitHubPRs(true)
          await fetchMergedPRs(true)
        }
        if (type === 'all' || type === 'pmc') {
          await fetchAllMembers()
        }
      }

      return response
    } catch (error) {
      console.error('Error triggering sync:', error)
      setError(error.userMessage || '触发数据同步失败')
      throw error
    } finally {
      setLoading(false)
      syncStatus.value.isRunning = false
    }
  }

  // 获取同步状态
  const fetchSyncStatus = async () => {
    try {
      const response = await dataApi.getSyncStatus()
      syncStatus.value.lastSync = response.lastUpdated
      syncStatus.value.stats = response.dataStats
      return response
    } catch (error) {
      console.error('Error fetching sync status:', error)
      throw error
    }
  }

  // 刷新所有数据
  const refreshAllData = async () => {
    try {
      setLoading(true)
      clearError()

      await Promise.allSettled([
        fetchJiraIssues(true),
        fetchGitHubPRs(true),
        fetchMergedPRs(true),
        fetchAllMembers()
      ])
    } catch (error) {
      console.error('Error refreshing data:', error)
      setError('刷新数据失败')
    } finally {
      setLoading(false)
    }
  }

  // 清除缓存
  const clearCache = async () => {
    try {
      await dataApi.clearServerCache()
      dataApi.clearCache()

      // 重置数据源状态
      dataSource.value = {
        jira: 'unknown',
        github: 'unknown',
        merged: 'unknown',
        members: 'unknown'
      }

      return true
    } catch (error) {
      console.error('Error clearing cache:', error)
      setError('清除缓存失败')
      throw error
    }
  }

  return {
    // 状态
    loading,
    error,
    jiraIssues,
    githubPRs,
    mergedPRs,
    customMembers,
    defaultPMCMembers,
    lastUpdated,
    dataSource,
    syncStatus,

    // 计算属性
    totalIssues,
    totalPRs,
    totalMergedPRs,
    totalCustomMembers,

    // 操作
    setLoading,
    setError,
    clearError,
    fetchJiraIssues,
    fetchGitHubPRs,
    fetchMergedPRs,
    fetchCustomMembers,
    addCustomMember,
    updateCustomMember,
    deleteCustomMember,
    fetchDefaultPMCMembers,
    addDefaultPMCMember,
    updateDefaultPMCMember,
    deleteDefaultPMCMember,
    fetchAllMembers,
    refreshAllData,
    triggerDataSync,
    fetchSyncStatus,
    clearCache
  }
}) 